import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useSupport } from '../hooks/useSupport';
import type { FaqPublico } from '../api/types';

/**
 * Janela de atendimento, no canto inferior direito de todo o site.
 *
 * O cliente escolhe a dúvida numa lista que a loja mantém e é respondido na
 * hora. No fim vem "Resolveu sua dúvida?" — e é essa resposta que diz à loja
 * quais textos funcionam. Quando não resolve, a conversa vai para o WhatsApp
 * com a pergunta já escrita, que é onde uma pessoa assume.
 *
 * Não exige conta: quem ainda não entrou é justamente quem mais pergunta
 * antes de decidir comprar.
 *
 * A conversa é uma lista de falas em vez de telas que se substituem — assim o
 * cliente continua vendo a resposta que leu enquanto decide se ela serviu.
 */

type Fala =
  | { tipo: 'bot'; texto: string }
  | { tipo: 'cliente'; texto: string }
  | { tipo: 'opcoes'; opcoes: { rotulo: string; acao: () => void; destaque?: boolean }[] }
  | { tipo: 'whatsapp'; link: string | null };

const SAUDACAO = 'Olá! Escolha abaixo a sua dúvida que eu respondo na hora.';

export function SupportWidget() {
  const { aberto, abrir, fechar } = useSupport();
  const [faq, setFaq] = useState<FaqPublico | null>(null);
  const [falas, setFalas] = useState<Fala[]>([]);
  const [encerrado, setEncerrado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [digitando, setDigitando] = useState(false);
  const fim = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  /** Já recebemos a lista alguma vez? Decide se uma falha vira erro na tela. */
  const jaVeio = useRef(false);

  /**
   * As perguntas são relidas a CADA abertura, não só na primeira.
   *
   * A janela vive fora das rotas e nunca remonta: quem cadastra uma pergunta no
   * painel navega por dentro do site, volta, e a janela continuava segurando a
   * lista que buscou antes — vazia. O sintoma é o pior possível para quem
   * acabou de cadastrar: parece que nada funcionou.
   *
   * Custa uma chamada pequena por abertura, e só quando alguém clica. A lista
   * anterior continua na tela enquanto a nova chega, para não piscar em branco.
   */
  useEffect(() => {
    if (!aberto) return;
    void api
      .faq()
      .then((r) => {
        jaVeio.current = true;
        setFaq(r);
      })
      .catch((e) => {
        // Falha na releitura com lista já em mãos não vira erro na tela: o
        // cliente continua usando o que está ali.
        if (!jaVeio.current) setErro(e instanceof ApiError ? e.message : 'Não consegui carregar as perguntas.');
      });
  }, [aberto]);

  useEffect(() => {
    if (aberto) fim.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [falas.length, digitando, aberto]);

  // Escape fecha, como em qualquer painel sobreposto.
  useEffect(() => {
    if (!aberto) return;
    const teclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    document.addEventListener('keydown', teclado);
    return () => document.removeEventListener('keydown', teclado);
  }, [aberto, fechar]);

  // Ao abrir, o foco vai para dentro do painel — quem navega por teclado não
  // deve continuar preso na página atrás.
  useEffect(() => {
    if (aberto) painel.current?.focus();
  }, [aberto]);

  const diz = (fala: Fala) => setFalas((prev) => [...prev, fala]);

  /**
   * Uma pausa curta antes da resposta, com os três pontinhos.
   *
   * Texto que aparece no mesmo instante do clique não é lido — o olho ainda
   * está no botão. A pausa é curta o bastante para não irritar e longa o
   * bastante para a resposta chegar como resposta.
   */
  const comPausa = (fn: () => void) => {
    setDigitando(true);
    window.setTimeout(() => {
      setDigitando(false);
      fn();
    }, 550);
  };

  function recomecar() {
    setFalas([]);
    setEncerrado(false);
    setErro(null);
  }

  async function escolher(id: number, pergunta: string) {
    setOcupado(true);
    setErro(null);
    diz({ tipo: 'cliente', texto: pergunta });
    try {
      const sessao = await api.abrirPergunta(id);
      comPausa(() => {
        diz({ tipo: 'bot', texto: sessao.answer });
        diz({ tipo: 'bot', texto: 'Resolveu sua dúvida?' });
        diz({
          tipo: 'opcoes',
          opcoes: [
            { rotulo: 'Sim', destaque: true, acao: () => void responder(sessao.sessionId, 'resolvida', 'Sim') },
            { rotulo: 'Não', acao: () => void responder(sessao.sessionId, 'nao_resolvida', 'Não') },
            {
              rotulo: 'Falar com atendimento',
              acao: () => void responder(sessao.sessionId, 'atendimento', 'Quero falar com o atendimento'),
            },
          ],
        });
      });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui abrir essa pergunta.');
    } finally {
      setOcupado(false);
    }
  }

  async function responder(
    sessionId: number,
    outcome: 'resolvida' | 'nao_resolvida' | 'atendimento',
    rotulo: string,
  ) {
    setOcupado(true);
    // As opções viram a escolha feita: botão que continua clicável depois de
    // respondido convida a responder duas vezes a mesma pergunta.
    setFalas((prev) => [...prev.filter((f) => f.tipo !== 'opcoes'), { tipo: 'cliente', texto: rotulo }]);

    try {
      const { whatsapp } = await api.fecharSessao(sessionId, outcome);

      comPausa(() => {
        if (outcome === 'resolvida') {
          diz({ tipo: 'bot', texto: 'Que bom! Se precisar de mais alguma coisa, é só escolher outra pergunta.' });
          setEncerrado(true);
          return;
        }
        if (outcome === 'nao_resolvida') {
          diz({ tipo: 'bot', texto: 'Que pena. Quer tentar outra pergunta ou prefere falar com o atendimento?' });
          diz({
            tipo: 'opcoes',
            opcoes: [
              { rotulo: 'Ver outras perguntas', acao: recomecar },
              { rotulo: 'Falar com atendimento', destaque: true, acao: () => void irParaAtendimento() },
            ],
          });
          return;
        }
        mostrarWhatsapp(whatsapp);
      });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui registrar a sua resposta.');
    } finally {
      setOcupado(false);
    }
  }

  /** Caminho do "Falar com atendimento" quando a sessão já foi encerrada. */
  async function irParaAtendimento() {
    setOcupado(true);
    setFalas((prev) => [
      ...prev.filter((f) => f.tipo !== 'opcoes'),
      { tipo: 'cliente', texto: 'Quero falar com o atendimento' },
    ]);
    try {
      const { whatsapp } = await api.linkAtendimento();
      comPausa(() => mostrarWhatsapp(whatsapp));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui abrir o atendimento.');
    } finally {
      setOcupado(false);
    }
  }

  function mostrarWhatsapp(link: string | null) {
    diz({
      tipo: 'bot',
      texto: link
        ? 'Sem problema. Uma pessoa assume daqui. Toque no botão para abrir a conversa no WhatsApp com a sua dúvida já escrita.'
        : 'O WhatsApp do atendimento ainda não foi cadastrado. Tente de novo mais tarde.',
    });
    diz({ tipo: 'whatsapp', link });
    setEncerrado(true);
  }

  const semPerguntas = faq && faq.items.length === 0;
  const mostrandoLista = falas.length === 0 && !digitando;

  return (
    <>
      <div className={`zap-janela ${aberto ? 'on' : ''}`} role="dialog" aria-label="Atendimento" aria-modal="false">
        <div className="zap-painel" ref={painel} tabIndex={-1}>
          <header className="zap-topo">
            <span className="zap-avatar" aria-hidden>
              <IconeBalao />
            </span>
            <div className="zap-topo-texto">
              <b>Atendimento</b>
              <span>
                <i className="zap-online" aria-hidden /> Respostas na hora
              </span>
            </div>
            <button className="zap-fechar" onClick={fechar} aria-label="Fechar o atendimento" title="Fechar">
              <IconeFechar />
            </button>
          </header>

          <div className="zap-corpo">
            {erro && <div className="zap-erro">{erro}</div>}

            <div className="zap-falas">
              <Balao autor="Atendimento" lado="deles">
                {SAUDACAO}
              </Balao>

              {falas.map((f, n) => {
                if (f.tipo === 'opcoes') {
                  return (
                    <div className="zap-opcoes" key={n}>
                      {f.opcoes.map((o) => (
                        <button
                          key={o.rotulo}
                          className={`zap-opcao ${o.destaque ? 'destaque' : ''}`}
                          disabled={ocupado}
                          onClick={o.acao}
                        >
                          {o.rotulo}
                        </button>
                      ))}
                    </div>
                  );
                }
                if (f.tipo === 'whatsapp') {
                  return (
                    <div className="zap-opcoes" key={n}>
                      {f.link && (
                        <a className="zap-cta" href={f.link} target="_blank" rel="noreferrer noopener">
                          <IconeWhatsapp /> Abrir o WhatsApp
                        </a>
                      )}
                    </div>
                  );
                }
                return (
                  <Balao key={n} autor={f.tipo === 'cliente' ? 'Você' : 'Atendimento'} lado={f.tipo === 'cliente' ? 'minha' : 'deles'}>
                    {f.texto}
                  </Balao>
                );
              })}

              {digitando && (
                <div className="zap-balao deles digitando" aria-label="Digitando">
                  <span />
                  <span />
                  <span />
                </div>
              )}
              <div ref={fim} />
            </div>
          </div>

          <footer className="zap-rodape">
            {!faq ? (
              <p className="zap-carregando">Carregando as perguntas…</p>
            ) : mostrandoLista ? (
              semPerguntas ? (
                <button className="zap-pergunta outra" disabled={ocupado} onClick={() => void irParaAtendimento()}>
                  Falar com o atendimento
                </button>
              ) : (
                <>
                  <p className="zap-dica">Escolha uma pergunta</p>
                  <div className="zap-perguntas">
                    {faq.items.map((q) => (
                      <button
                        key={q.id}
                        className="zap-pergunta"
                        disabled={ocupado}
                        onClick={() => void escolher(q.id, q.question)}
                      >
                        {q.question}
                      </button>
                    ))}
                    <button className="zap-pergunta outra" disabled={ocupado} onClick={() => void irParaAtendimento()}>
                      Minha dúvida não está na lista
                    </button>
                  </div>
                </>
              )
            ) : (
              encerrado && (
                <button className="zap-pergunta" onClick={recomecar}>
                  Tirar outra dúvida
                </button>
              )
            )}
          </footer>
        </div>
      </div>

      <button
        className={`zap-botao ${aberto ? 'on' : ''}`}
        onClick={aberto ? fechar : abrir}
        aria-expanded={aberto}
        aria-label={aberto ? 'Fechar o atendimento' : 'Abrir o atendimento'}
        title={aberto ? 'Fechar' : 'Dúvidas'}
      >
        <span className="zap-botao-icone abre">
          <IconeBalao />
        </span>
        <span className="zap-botao-icone fecha">
          <IconeFechar />
        </span>
      </button>
    </>
  );
}

function Balao({ autor, lado, children }: { autor: string; lado: 'minha' | 'deles'; children: string }) {
  return (
    <div className={`zap-balao ${lado}`}>
      <span className="zap-autor">{autor}</span>
      {/* pre-wrap: a resposta é escrita em parágrafos no painel, e sem isto
          tudo colapsa numa linha só. */}
      <span className="zap-texto">{children}</span>
    </div>
  );
}

function IconeBalao() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 11.6a8.4 8.4 0 0 1-8.4 8.4 8.3 8.3 0 0 1-3.8-.9L3 21l1.9-5.4A8.4 8.4 0 1 1 21 11.6z"
      />
      <circle cx="8.6" cy="11.7" r="1.1" fill="currentColor" />
      <circle cx="12.3" cy="11.7" r="1.1" fill="currentColor" />
      <circle cx="16" cy="11.7" r="1.1" fill="currentColor" />
    </svg>
  );
}

function IconeFechar() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function IconeWhatsapp() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5l-.9-2.1c-.2-.5-.4-.5-.6-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.2.2 2.1 3.2 5 4.4.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        d="M12 3.2a8.8 8.8 0 0 0-7.5 13.4L3.3 20.7l4.2-1.1A8.8 8.8 0 1 0 12 3.2z"
      />
    </svg>
  );
}
